import { useState } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent } from "@/components/ui/card";
import { User, FileText, Images, Target, Sprout, Share2, Users } from "lucide-react";
import { Profile } from "@/hooks/useProfile";
import { supabase } from "@/integrations/supabase/client";
import { ProfileForm } from '../ProfileForm';
import { FarmImagesUpload } from '../FarmImagesUpload';
import { FarmGoalsManager } from '../FarmGoalsManager';
import { CropPlanTab } from '../CropPlanTab';
import { SocialAccountsFeatureSection } from './SocialAccountsFeatureSection';
import { DiscoverabilitySettings } from './DiscoverabilitySettings';
import { ProfileTags } from './ProfileTags';
import { CommunitySelector } from './CommunitySelector';
import { useCropPlan } from '@/hooks/useCropPlan';

interface FarmImage {
  id: string;
  url: string;
  role: 'profile_icon' | 'main_farm' | 'farm_extra';
}

interface ProfileEditTabsProps {
  profile: Profile;
  editedProfile: Profile;
  setEditedProfile: (profile: Profile) => void;
  avatarUploading: boolean;
  onAvatarUpload: (event: React.ChangeEvent<HTMLInputElement>) => void;
}

export const ProfileEditTabs = ({
  profile,
  editedProfile,
  setEditedProfile,
  avatarUploading,
  onAvatarUpload
}: ProfileEditTabsProps) => {
  const [activeTab, setActiveTab] = useState("profile-info");
  const { userCropPlans, removeCropFromPlan } = useCropPlan();

  // Convert existing farm_images to new structure if needed
  const convertLegacyImages = (farmImages: any): FarmImage[] => {
    if (!farmImages || !Array.isArray(farmImages)) return [];
    
    return farmImages.map((img: any, index: number) => {
      if (img.role) return img; // Already in new format
      
      // Convert legacy format
      let role: 'profile_icon' | 'main_farm' | 'farm_extra';
      if (img.is_logo || index === 0) {
        role = 'profile_icon';
      } else if (index === 1) {
        role = 'main_farm';
      } else {
        role = 'farm_extra';
      }
      
      return {
        id: img.id || `legacy-${index}`,
        url: img.url,
        role
      };
    });
  };

  const currentImages = convertLegacyImages(editedProfile.farm_images);

  const handleUpdateProfile = (field: keyof typeof editedProfile, value: string) => {
    if (!editedProfile) return;
    setEditedProfile({
      ...editedProfile,
      [field]: value
    });
  };

  const handleUpdateHardinessZone = async (zoneId: string) => {
    if (!editedProfile) return;
    
    // Fetch the zone code to sync the usda_zone field
    try {
      const { data: zoneData } = await supabase
        .from('hardiness_zones')
        .select('zone_code')
        .eq('id', zoneId)
        .single();
      
      setEditedProfile({
        ...editedProfile,
        hardiness_zone_id: zoneId,
        usda_zone: zoneData?.zone_code || editedProfile.usda_zone
      });
    } catch (error) {
      console.error('Error fetching zone code:', error);
      // Fallback: just set the hardiness_zone_id
      setEditedProfile({
        ...editedProfile,
        hardiness_zone_id: zoneId
      });
    }
  };

  const handleGoalsChange = (goals: string[]) => {
    if (!editedProfile) return;
    setEditedProfile({
      ...editedProfile,
      farm_goals: goals
    });
  };

  const handleImagesUpdate = (images: FarmImage[], profileIconUrl?: string, mainFarmUrl?: string) => {
    if (!editedProfile) return;
    
    setEditedProfile({
      ...editedProfile,
      farm_images: images,
      avatar_url: profileIconUrl || editedProfile.avatar_url,
      farm_logo_url: mainFarmUrl || editedProfile.farm_logo_url
    });
  };

  const handleDiscoverabilityToggle = (isDiscoverable: boolean) => {
    if (!editedProfile) return;
    setEditedProfile({
      ...editedProfile,
      is_discoverable: isDiscoverable
    });
  };

  const handleLocationToggle = (locationVisible: boolean) => {
    if (!editedProfile) return;
    setEditedProfile({
      ...editedProfile,
      location_visible: locationVisible
    });
  };

  const handleTagsUpdate = (tags: string[]) => {
    if (!editedProfile) return;
    setEditedProfile({
      ...editedProfile,
      profile_tags: tags
    });
  };

  const handleRemoveCrop = (cropId: string, cropName: string) => {
    removeCropFromPlan(cropId);
  };

  return (
    <Card>
      <CardContent className="p-0">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-5 bg-muted/50 p-1 h-auto rounded-lg gap-1">
            <TabsTrigger 
              value="profile-info" 
              className="flex items-center justify-center gap-2 text-sm font-medium py-3 px-2 rounded-md data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              <User size={16} />
              <span className="hidden sm:inline">Profile</span>
            </TabsTrigger>
            <TabsTrigger 
              value="images" 
              className="flex items-center justify-center gap-2 text-sm font-medium py-3 px-2 rounded-md data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              <Images size={16} />
              <span className="hidden sm:inline">Images</span>
            </TabsTrigger>
            <TabsTrigger 
              value="goals" 
              className="flex items-center justify-center gap-2 text-sm font-medium py-3 px-2 rounded-md data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              <Target size={16} />
              <span className="hidden sm:inline">Goals</span>
            </TabsTrigger>
            <TabsTrigger 
              value="crop-plan" 
              className="flex items-center justify-center gap-2 text-sm font-medium py-3 px-2 rounded-md data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              <Sprout size={16} />
              <span className="hidden sm:inline">Crops</span>
            </TabsTrigger>
            <TabsTrigger 
              value="communities" 
              className="flex items-center justify-center gap-2 text-sm font-medium py-3 px-2 rounded-md data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              <Users size={16} />
              <span className="hidden sm:inline">Community</span>
            </TabsTrigger>
            <TabsTrigger 
              value="about" 
              className="flex items-center justify-center gap-2 text-sm font-medium py-3 px-2 rounded-md data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              <FileText size={16} />
              <span className="hidden sm:inline">Settings</span>
            </TabsTrigger>
          </TabsList>

          <TabsContent value="profile-info" className="p-4">
            <div className="space-y-3">
              <ProfileForm 
                profile={editedProfile}
                avatarUploading={avatarUploading}
                onAvatarUpload={onAvatarUpload}
                onUpdateProfile={handleUpdateProfile}
                onUpdateHardinessZone={handleUpdateHardinessZone}
              />
            </div>
          </TabsContent>

          <TabsContent value="images" className="p-6">
            <div className="space-y-4">
              <h3 className="text-lg font-medium">Farm Images</h3>
              <FarmImagesUpload
                currentImages={currentImages}
                onImagesUpdate={handleImagesUpdate}
                isEditing={true}
              />
            </div>
          </TabsContent>

          <TabsContent value="goals" className="p-6">
            <div className="space-y-4">
              <h3 className="text-lg font-medium">Farm Goals</h3>
              <FarmGoalsManager
                goals={editedProfile.farm_goals || []}
                onGoalsChange={handleGoalsChange}
                isEditing={true}
              />
            </div>
          </TabsContent>

          <TabsContent value="crop-plan" className="p-6">
            <div className="space-y-4">
              <h3 className="text-lg font-medium">Crop Plan Management</h3>
              <CropPlanTab 
                cropPlans={userCropPlans}
                onRemoveCrop={handleRemoveCrop}
                isEditing={true}
              />
            </div>
          </TabsContent>

          <TabsContent value="communities" className="p-6">
            <div className="space-y-4">
              <h3 className="text-lg font-medium">Communities</h3>
              <CommunitySelector profileId={editedProfile.id} />
            </div>
          </TabsContent>

          <TabsContent value="about" className="p-4">
            <div className="space-y-4">
              <div>
                <h3 className="text-base font-medium mb-2 flex items-center gap-2">
                  <Share2 className="w-4 h-4" />
                  Profile Visibility & Tags
                </h3>
                <div className="space-y-3">
                  <DiscoverabilitySettings
                    profile={editedProfile}
                    onToggle={handleDiscoverabilityToggle}
                    onLocationToggle={handleLocationToggle}
                  />
                  <ProfileTags
                    profile={editedProfile}
                    onTagsUpdate={handleTagsUpdate}
                  />
                </div>
              </div>
              
              <div>
                <h3 className="text-base font-medium mb-2">Social Media Accounts</h3>
                <SocialAccountsFeatureSection isEditMode={true} />
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
};